import type { BadgeProps } from '@wartek-id/badge'

import {
  GpkPraktikKinerjaGradingStateEnum,
  GpkPerilakuKerjaGradingStateEnum,
} from 'types/kinerja-gpk'

import type {
  IReflectionImprovementTypes,
  TWorkBehaviorImpactScore,
  TWorkBehaviorImplementationScore,
  TWorkBehaviorResult,
} from 'types/kinerja-pelaksanaan'
import type { TObservationResultTypes } from 'types/kinerja-pelaksanaan'
import type { KinerjaStatusGpk } from 'types/kinerja-gpk'
import type { NormalizedRefleksiFields } from 'app/KinerjaGpkPage/types/pelaksanaan'

export const GPK_PEGAWAI_TUGAS_POKOK = [
  'Terlaksananya identifikasi kebutuhan belajar dan perencanaan pembelajaran dalam mewujudkan layanan pembelajaran bagi murid penyandang disabilitas',
  'Terlaksananya pembelajaran dalam mewujudkan layanan pembelajaran bagi murid penyandang disabilitas',
  'Terlaksananya penilaian hasil pembelajaran dalam mewujudkan layanan pembelajaran bagi murid penyandang disabilitas',
  'Terlaksananya pembimbingan atau pendampingan guru lainnya dalam mewujudkan layanan pembelajaran bagi murid penyandang disabilitas',
]

export const GPK_PEGAWAI_PRAKTIK_KINERJA_ICONS: Record<string, string> = {
  'Asesmen Pendidikan Khusus': 'img-reflection-documents-magnifying-glass.png',
  'Program Pembelajaran Individual': 'img-document-skills-improvement.png',
  'Interaksi Inklusif': 'img-sharing-chat-emoji-characters.png',
  'Bimbingan Sosial Emosional': 'img-heart-empathic-give.png',
  'Pembelajaran Adaptif': 'img-community-character-emoji-interaction.png',
  'Program Kompensatorik': 'img-flow-process.png',
  'Umpan Balik Guru': 'img-chat-talk.png',
  'Presentasi Pelibatan': 'img-presentation-data-chart.png',
}

export const GPK_PEGAWAI_PERENCANAAAN_STATUS_LABELS: Record<
  KinerjaStatusGpk,
  string
> = {
  NOT_FOUND: 'Belum diajukan',
  DRAFT: 'Belum diajukan',
  WAITING_APPROVAL: 'Menunggu pemeriksaan',
  WAITING_APPROVAL_PPK:
    'Sudah diperiksa Tim Kinerja dan perlu disetujui Pejabat Penilai Kinerja',
  REVISION_TK: 'Revisi dari Tim Kinerja',
  REVISION_PPK: 'Revisi dari Pejabat Penilai Kinerja',
  APPROVED: 'Disetujui Pejabat Penilai Kinerja',
  READY: 'Siap untuk pelaksanaan',
}

export const GPK_PELAKSANAAN_GRADING_STATE_LABELS: Record<
  GpkPraktikKinerjaGradingStateEnum,
  string
> = {
  [GpkPraktikKinerjaGradingStateEnum.OBSERVASI]: 'Observasi',
  [GpkPraktikKinerjaGradingStateEnum.NILAI_TINDAK_LANJUT]:
    'Rencana Tindak Lanjut',
  [GpkPraktikKinerjaGradingStateEnum.NILAI_REFLEKSI_TINDAK_LANJUT]:
    'Refleksi dan Evaluasi Tindak Lanjut',
  [GpkPraktikKinerjaGradingStateEnum.SELESAI]: 'Sudah Selesai',
}

export const GPK_PERILAKU_KERJA_GRADING_STATE_LABELS: Record<
  GpkPerilakuKerjaGradingStateEnum,
  string
> = {
  [GpkPerilakuKerjaGradingStateEnum.BELUM_DINILAI]: 'Belum Selesai',
  [GpkPerilakuKerjaGradingStateEnum.SELESAI]: 'Sudah Selesai',
}

export const GPK_RATING_OBSERVASI_OPTIONS: Record<
  TObservationResultTypes,
  string
> = {
  NOT_STARTED: 'Belum Dilakukan',
  START_NOT_EFFECTIVE: 'Dilakukan tetapi Belum Efektif',
  START_EFFECTIVE: 'Dilakukan dan Efektif',
}

export const GPK_OBSERVATION_RESULT_BADGE: Record<
  TObservationResultTypes,
  { text: string; variant: BadgeProps['variant'] }
> = {
  NOT_STARTED: {
    text: 'Belum Dilakukan',
    variant: 'critical',
  },
  START_NOT_EFFECTIVE: {
    text: 'Dilakukan tapi Belum Efektif',
    variant: 'warning',
  },
  START_EFFECTIVE: {
    text: 'Dilakukan dan Efektif',
    variant: 'success',
  },
}

export const GPK_DUKUNGAN_UPAYA_TINDAK_LANJUT = [
  'Sumber daya untuk melakukan kegiatan belajar/praktik',
  'Penyediaan atau pembelian alat dukung pembelajaran',
  'Penyediaan atau akses ke sumber belajar melalui satuan pendidikan',
  'Izin untuk mengikuti pelatihan di luar satuan pendidikan',
  'Memperoleh mentoring dari Kepala Sekolah, Guru atau Pemateri Eksternal, atau Manajemen',
]

export const GPK_PEMANTAUAN_REFLEKSI_FORM_FIELDS: Record<
  NormalizedRefleksiFields,
  string
> = {
  inspiration: 'Inspirasi baru yang Guru dapatkan dari upaya tindak lanjut:',
  achievement:
    'Berdasarkan inspirasi yang didapatkan, perubahan praktik Guru di ruang kelas/satuan pendidikan:',
  challenge:
    '3 tantangan paling sulit yang Guru hadapi dalam melakukan perubahan:',
  improvementPlan:
    'Rencana Guru dalam mengatasi tantangan agar bisa memastikan perubahan terjadi:',
}

export const GPK_UPAYA_REFLEKSI_OPTIONS: Array<{
  value: IReflectionImprovementTypes
  label: string
  details: Array<string>
}> = [
  {
    value: 'MEET_EXPECTATION',
    label: 'Sadar tantangan dan penyebabnya berada di luar kendali diri',
    details: [
      'Menjelaskan penyebab tantangan yang berasal dari faktor eksternal (sarana prasarana, anggaran, koneksi sinyal, dll).',
    ],
  },
  {
    value: 'EXCEED_EXPECTATION',
    label: 'Sadar tantangan dan penyebabnya berada di dalam kendali diri',
    details: [
      'Menjelaskan penyebab tantangan yang berasal dari kemampuan atau keterbatasan dirinya, bukan dari faktor eksternal.',
    ],
  },
  {
    value: 'EXCEPTIONAL',
    label:
      'Sadar tantangan dan penyebabnya berada di dalam kendali diri serta dampaknya',
    details: [
      'Menjelaskan penyebab tantangan yang berasal dari kemampuan atau keterbatasan dirinya, bukan dari faktor eksternal.',
      'Menjelaskan dampak kualitas pembelajaran yang dirasakan murid disertai data, bukti lapangan, riset, atau Rapor Pendidikan.',
    ],
  },
]

export const GPK_UPAYA_MEMPELAJARI_OPTIONS: Array<{
  value: IReflectionImprovementTypes
  label: string
  details: Array<string>
}> = [
  {
    value: 'MEET_EXPECTATION',
    label: 'Melakukan upaya tindak lanjut',
    details: ['Melakukan upaya tindak lanjut yang sudah disepakati.'],
  },
  {
    value: 'EXCEED_EXPECTATION',
    label: 'Mempraktikkan inspirasi dari upaya tindak lanjut',
    details: [
      'Mendapatkan inspirasi dari upaya tindak lanjut yang telah dilakukan.',
      'Mempraktikkan inspirasi dari upaya tindak lanjut yang telah dilakukan.',
    ],
  },
  {
    value: 'EXCEPTIONAL',
    label: 'Membagikan praktik baik hasil inspirasi dari upaya tindak lanjut',
    details: [
      'Mempraktikkan inspirasi dari upaya tindak lanjut yang telah dilakukan.',
      'Membagikan praktik baik melalui komunitas belajar di dalam dan/atau luar sekolah.',
    ],
  },
]

export const GPK_PENINGKATAN_KINERJA_OPTIONS: Array<{
  value: IReflectionImprovementTypes
  label: string
  details: Array<string>
}> = [
  {
    value: 'MEET_EXPECTATION',
    label: 'Ada upaya melakukan peningkatan kinerja',
    details: [
      'Ada tindakan yang diambil untuk meningkatkan efektivitas, efisiensi, atau kualitas kinerja dalam suatu kegiatan atau pekerjaan.',
    ],
  },
  {
    value: 'EXCEED_EXPECTATION',
    label:
      'Upaya melakukan peningkatan kinerja sejalan dengan ekspektasi atasan',
    details: [
      'Ada tindakan yang diambil untuk meningkatkan efektivitas, efisiensi, atau kualitas kinerja dalam suatu kegiatan atau pekerjaan.',
      'Upaya melakukan peningkatan kinerja memenuhi atau melampaui standar yang disepakati bersama atasan.',
    ],
  },
  {
    value: 'EXCEPTIONAL',
    label:
      'Upaya melakukan peningkatan kinerja mendapatkan respon positif dari murid',
    details: [
      'Upaya melakukan peningkatan kinerja memenuhi atau melampaui standar yang disepakati bersama atasan.',
      'Upaya melakukan peningkatan kinerja telah berjalan secara efektif sehingga diterima dan dihargai oleh para murid.',
    ],
  },
]

export const GPK_PERILAKU_KERJA_RESULT: Record<TWorkBehaviorResult, string> = {
  BELOW_EXPECTATION: 'Di Bawah Ekspektasi',
  MEET_EXPECTATION: 'Sesuai Ekspektasi',
  EXCEED_EXPECTATION: 'Di Atas Ekspektasi',
}

export const GPK_PERILAKU_KERJA_IMPLEMENTATION_OPTIONS: Array<{
  value: TWorkBehaviorImplementationScore
  label: string
}> = [
  { value: 1, label: 'Tidak melakukan upaya mempelajari' },
  { value: 2, label: 'Ada upaya tapi kesulitan mempelajari' },
  { value: 3, label: 'Menunjukkan ada perubahan perilaku' },
  { value: 4, label: 'Menunjukkan perilaku nyata tapi belum konsisten' },
  { value: 5, label: 'Menunjukkan perilaku nyata dan konsisten' },
]

export const GPK_PERILAKU_KERJA_IMPACT_OPTIONS: Array<{
  value: TWorkBehaviorImpactScore
  label: string
}> = [
  { value: 1, label: 'Terlihat upayanya tapi tidak berdampak' },
  { value: 2, label: 'Terlihat upayanya yang berpotensi berdampak' },
  { value: 3, label: 'Berdampak kecil pada kinerjanya atau timnya' },
  { value: 4, label: 'Berdampak besar pada kinerjanya atau timnya' },
  { value: 5, label: 'Berdampak pada Kinerja Satuan Pendidikan' },
]
